<!-- StatusBar.vue -->
<template>
  <!-- Every cell must be able to shrink: this bar sits in the center column,
       and a cell that refuses to get smaller (an unconstrained width, a
       nowrap number) raises the whole column's min-content width — which used
       to push the right sidebar past the viewport edge and make it jitter as
       the scale readout's text length changed while zooming. -->
  <div class="status-bar pl-2 pb-1 flex w-full min-w-0 gap-2 overflow-hidden whitespace-nowrap">
    <Tooltip :content="tooltipContent" :show="showTooltip" />
    <div class="text-xs min-w-0 truncate">version: {{appVersion}}</div>
    <div
      class="flex shrink-0 items-center gap-1"
      :class="designModified ? 'text-amber-300' : 'text-emerald-300'"
      :title="designModified ? 'Design has unsaved changes' : 'Design is saved'"
      aria-live="polite"
    >
      <span aria-hidden="true">{{ designModified ? '●' : '✓' }}</span>
      <span>{{ designModified ? 'unsaved changes' : 'saved' }}</span>
    </div>
    <CanvasModeSwitch />
    <button v-if="graphMode" type="button"
      class="shrink-0 rounded border border-gray-600 px-2 leading-5 text-gray-300 hover:bg-gray-700"
      title="Arrange the nodes around the page (Shift+L)"
      @click="$emit('arrange-graph')">Arrange</button>
    <!-- <div class="w-20 border flex">
      <div class="w-5 text-center" :class="{ 'bg-green-700': has_action }">A</div>
      <div class="w-5 text-center" :class="{ 'bg-green-700': has_command }">C</div>
      <div class="w-5 text-center" :class="{ ' bg-orange-900': has_hass_entity }">H</div>
      <div class="w-5 text-center" :class="{ ' bg-orange-700': has_hass_event }">E</div>
      <div class="w-5 text-center" :class="{ ' bg-orange-500': has_hass_service }">S</div>
    </div> -->
    <div class="flex-1 min-w-0 truncate pl-2">{{ status }}</div>
    <!-- Fixed decimals: the raw stage scale (0.9755530000001) changed the
         readout's pixel width on every zoom step. -->
    <div class="min-w-0 truncate tabular-nums">scale:{{ scaleLabel }}</div>
    <div class="flex min-w-0 gap-2 overflow-hidden tabular-nums">
      <div class="min-w-0 truncate">x:{{ node_x }}</div>
      <div class="min-w-0 truncate">y:{{ node_y }}</div>
      <div class="min-w-0 truncate">w:{{ node_w }}</div>
      <div class="min-w-0 truncate">h:{{ node_h }}</div>
    </div>
  </div>
</template>

<script>
import HaspObject from '../Hasp/HaspObject';
import Tooltip from './Tooltip.vue';
import CanvasModeSwitch from './CanvasModeSwitch.vue';
import { useCanvasModeStore } from '../stores/canvasModeStore.js';

export default {
  name: 'StatusBar',
  components: {
    Tooltip,
    CanvasModeSwitch,
  },
  props: {
    status: {
      type: String,
      required: true
    },
    stage_scale: {
      type: Number,
      default: 7,

    },
    selectedNode: {
      type: HaspObject,
      // required: true,
    },
    designModified: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      appVersion: __APP_VERSION__,
      showTooltip: false,
      tooltipContent: "Tooltip Content",
      node_x: 0,
      node_y: 0,
      node_w: 0,
      node_h: 0,
      has_action: false,
      has_command: false,
      has_hass_entity: false,
      has_hass_event: false,
      has_hass_service: false,
      has_hass_service_action: false,
    }
  },
  emits: ['arrange-graph'],
  computed: {
    // A node graph shows (Logic or HA), so its nodes can be arranged.
    graphMode() {
      return useCanvasModeStore().mode !== 'design';
    },
    // Constant-width readout — see the template comment.
    scaleLabel() {
      const scale = Number(this.stage_scale);
      return Number.isFinite(scale) ? scale.toFixed(2) : '—';
    },
  },
  mounted() {
    if (this.selectedNode !== undefined) {
      this.node_x = Math.round(this.selectedNode.x());
      this.node_y = Math.round(this.selectedNode.y());
      this.node_w = Math.round(this.selectedNode.width());
      this.node_h = Math.round(this.selectedNode.height());
      this.has_action = this.selectedNode.action !== undefined && this.selectedNode.action !== '';
      this.has_command = this.selectedNode.command !== undefined && this.selectedNode.command !== '';
      this.has_hass_entity = this.selectedNode.hass_entityid !== undefined && this.selectedNode.hass_entityid !== '';
      this.has_hass_event = this.selectedNode.hass_event !== undefined && this.selectedNode.hass_event !== '';
      this.has_hass_service = this.selectedNode.hass_service !== undefined && this.selectedNode.hass_service !== '';
    }
  }
}
</script>

<style>
.status-bar {
  /* background-color: #333; */
  color: #fff;
  /* padding: 5px; */
  display: flex;
  /* Ensure the status bar items are displayed in a row */
  align-items: center;
  /* Center the items vertically */
}

.status-bar p {
  margin: 0;
  /* Remove default margin for the paragraph */
}
</style>
